import { useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";

type ActiveDisclaimer = { version: number; text: string } | null;

export function useDisclaimer() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [disclaimer, setDisclaimer] = useState<ActiveDisclaimer>(null);
  const [accepted, setAccepted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Aktive Disclaimer-Version laden
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase
        .from("disclaimers")
        .select("version, text")
        .eq("is_active", true)
        .maybeSingle();

      if (!alive) return;
      if (error) {
        console.warn("Load disclaimer failed", error.message);
        setDisclaimer(null);
        setAccepted(true); // wenn kein aktiver Disclaimer existiert, nichts blocken
        setLoading(false);
        return;
      }

      if (!data) {
        setDisclaimer(null);
        setAccepted(true);
        setLoading(false);
        return;
      }

      setDisclaimer({ version: data.version, text: data.text });

      if (!user) {
        // nicht eingeloggt → Disclaimer im Protected-Bereich greift erst nach Login
        setAccepted(false);
        setLoading(false);
        return;
      }

      // Hat der User diese Version akzeptiert?
      const { data: acc, error: accErr } = await supabase
        .from("disclaimer_acceptances")
        .select("id")
        .eq("user_id", user.id)
        .eq("disclaimer_version", data.version)
        .maybeSingle();

      if (accErr) {
        console.warn("Check acceptance failed", accErr.message);
        setAccepted(false);
      } else {
        setAccepted(!!acc);
      }
      setLoading(false);
    })();

    return () => {
      alive = false;
    };
  }, [user?.id]);

  const accept = useCallback(async () => {
    if (!user || !disclaimer) return { ok: false, error: "not_ready" };
    const { error } = await supabase.from("disclaimer_acceptances").insert({
      user_id: user.id,
      disclaimer_version: disclaimer.version,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
    if (error) {
      return { ok: false, error: error.message };
    }
    setAccepted(true);
    return { ok: true };
  }, [user, disclaimer]);

  return {
    loading,
    disclaimer,   // {version, text} | null
    accepted,     // true/false
    error,
    accept,       // () => Promise<{ok: boolean, error?: string}>
  };
}
